import {
  EnergyLabelMetadata,
  energyLabelResponseSchema,
  EnergyLabelResults,
} from "./energyLabelResponseSchema";
import { getProductType } from "../utils";
import _ from "lodash";

const timeout = 1500;
const yourekoHost = "https://api.youreko.com";
const apiVersion = "v1";
const region = "en-gb";
const partnerName = "john-lewis";

export type EnergyLabelsResponse = {
  responseMetaData?: EnergyLabelMetadata;
  results?: EnergyLabelResults;
  error?: string;
};

type EnergyLabelsBaseOptions = {
  showTemplates: boolean;
  productTypes: Array<string | undefined>;
  productTitles: string;
};

export type EnergyLabelsRequestParams = {
  [index: string]: number | null;
  customCyclesPerYear: number | null;
  customEnergyCostYears: number | null;
  customTariff: number | null;
};

const fetchEnergyLabels = async (
  sellingProductType: string,
  title: string,
  usageParams: EnergyLabelsRequestParams | null = null,
): Promise<EnergyLabelsResponse> => {
  const path = `${yourekoHost}/${apiVersion}/${region}/${partnerName}/energy-labels.json`;

  const baseOptions: EnergyLabelsBaseOptions = {
    showTemplates: false,
    productTypes: [getProductType(sellingProductType)],
    productTitles: title,
  };

  const url = `${path}?${optionsToQueryString(baseOptions, usageParams)}`;
  const { controller, timeoutId } = setControllerTimeout();

  return fetch(url, {
    signal: controller.signal,
  })
    .then(async (res) => {
      if (res.ok) {
        const result: unknown = await res.json();
        const parsed = energyLabelResponseSchema.safeParse(result);

        if (parsed.success) {
          const thisProduct = parsed.data.results[title];

          if (!thisProduct || !thisProduct.productFound) {
            throw new Error("Product not found");
          }

          if (!thisProduct.hasEnergyData) {
            throw new Error("No energy data found");
          }

          return {
            responseMetaData: parsed.data.responseMetaData,
            results: parsed.data.results,
          };
        }

        throw new Error("Energy Label response parse failed");
      }

      throw new Error("Energy Label request failed");
    })
    .catch((e) => {
      if (e.name === "AbortError") {
        return { error: "Energy Label request timed out" };
      }

      return { error: e.message || "Energy Label request failed" };
    })
    .finally(() => {
      clearTimeout(timeoutId);
    });
};

const setControllerTimeout = () => {
  const controller = new AbortController();

  const timeoutId = setTimeout(() => {
    controller.abort();
  }, timeout);

  return { controller, timeoutId };
};

const formatUsageParams = (usageParams: EnergyLabelsRequestParams | null) => {
  if (usageParams) {
    const { customCyclesPerYear, customEnergyCostYears, customTariff } =
      usageParams;
    return {
      customCyclesPerYear,
      customEnergyCostYears: customEnergyCostYears
        ? _.clamp(customEnergyCostYears, 0, 20)
        : null,
      customTariff: customTariff ? customTariff / 100 : null,
    };
  }

  return {};
};

const optionsToQueryString = (
  options: EnergyLabelsBaseOptions,
  customUsageParams: EnergyLabelsRequestParams | null,
) => {
  const { showTemplates } = options;

  let query = `showTemplates=${showTemplates}`;

  const baseOptionsParams = {
    "productTypes[]": encodeURIComponent(options.productTypes.join(",")),
    productTitles: encodeURIComponent(options.productTitles),
  };
  const formattedUsageParams = formatUsageParams(customUsageParams);
  const combinedParams = {
    ...baseOptionsParams,
    ...formattedUsageParams,
  };

  for (const [param, value] of Object.entries(combinedParams)) {
    if (!value) continue;
    query += `&${param}=${value}`;
  }

  return query;
};

export default fetchEnergyLabels;
